// React and related hooks
import React, { useEffect, useMemo, useRef, useState } from "react";

// Custom 3D managers and utilities
import { ModelManager } from "./SetUps/ModelManager";
import { downloadImage } from "../helpers/CONSTANT";
import { sceneManager } from "./SetUps/SceneManager";
import { CubeManager } from "./SetUps/CubeManager";
import { MouseEventHandlers } from "./Events/MouseEventHandlers";
import { POLYGONS, VARS } from "./Global/variables";
import { useRecoilState, useSetRecoilState } from "recoil";
import { clearUndoStack } from "./Global/UndoManager"; 
// Recoil state management
import {
    avaArea,
    polygonsState,
    selectedIndicesState,
    solarPanel,
    setBackInput,
    deletePanelIndex,
    panelSettings
} from "./ReactStates/states";

import {
    addObstaclesSelector,
    addPanelSelector,
    deleteObstacleSelector,
    deletePanelsSelector,
    updatePolygonDetailsSelector,
    updatePolygonSetbacksSelector
} from "./ReactStates/updates";

import { loadDrawings } from "./DataUtils/LoadData";

// Main component
export default function Main({
    setModelProgress,
    currentDesign,
    currentDrawingModeRef,
    updateDrawingMode,
    obstacleTypeRef,
    setDeleteConfirmationVisible,
    panelList,
    arePanelsOK,
    setPanelsOK
}) {
    // Ref for container element
    const refContainer = useRef(null);

    // Initialize scene manager (singleton)
    const SceneManager = useMemo(() => sceneManager, []);

    // Destructure required objects from the scene
    const { scene, camera, renderer, controls, labelRenderer } = SceneManager.getSceneObjects();

    // Initialize cube manager for orientation cube UI
    const cubeManager = useMemo(() => new CubeManager(camera, controls), [scene]);

    // Load 3D model meshes
    const modelMeshs = useMemo(() => new ModelManager(scene, setModelProgress, currentDesign), [scene]);

    // Recoil state hooks
    const [polygons, setPolygons] = useRecoilState(polygonsState);
    const setObstacles = useSetRecoilState(addObstaclesSelector);
    const deleteObstacle = useSetRecoilState(deleteObstacleSelector);
    const setPanels = useSetRecoilState(addPanelSelector);
    const deletePanels = useSetRecoilState(deletePanelsSelector);
    const setDeletePanels = useSetRecoilState(deletePanelIndex);
    const [selectedIndices, setSelectedIndices] = useRecoilState(selectedIndicesState);
    const setPolygonDetails = useSetRecoilState(updatePolygonDetailsSelector);
    const addSetback = useSetRecoilState(updatePolygonSetbacksSelector);
    const setShowPanelSettings = useSetRecoilState(panelSettings);
    const [inputValues, setInputValues] = useRecoilState(setBackInput);
    const setAvailableArea = useSetRecoilState(avaArea);
    const [panel, setPanel] = useRecoilState(solarPanel);

    // Local state
    const [mouseDown, setMouseDown] = useState(false);
    const selectedIndicesRef = useRef(selectedIndices);
    let mouseEventHandlers = null;

    // Screenshot capture logic
    const captureScreenshot = () => {
        const canvas = renderer.domElement;

        requestAnimationFrame(() => {
            // Create composite canvas for the main scene
            const compositeCanvas = document.createElement("canvas");
            const compositeContext = compositeCanvas.getContext("2d");
            compositeCanvas.width = canvas.width;
            compositeCanvas.height = canvas.height;
            compositeContext.drawImage(canvas, 0, 0);

            const thumbnailCanvas = document.createElement("canvas");
            const thumbnailContext = thumbnailCanvas.getContext("2d");
            thumbnailCanvas.width = canvas.width;
            thumbnailCanvas.height = canvas.height;

            // Define thumbnail size and position
            const thumbnailSize = 100;
            const thumbnailMargin = 30;
            const thumbnailX = thumbnailCanvas.width - thumbnailSize - thumbnailMargin;
            const thumbnailY = thumbnailCanvas.height - thumbnailSize - thumbnailMargin;

            // Draw thumbnail from main canvas
            thumbnailContext.drawImage(canvas, thumbnailX, thumbnailY, thumbnailSize, thumbnailSize);

            // Create final canvas with main + thumbnail
            const finalCompositeCanvas = document.createElement("canvas");
            const finalCompositeContext = finalCompositeCanvas.getContext("2d");
            finalCompositeCanvas.width = canvas.width;
            finalCompositeCanvas.height = canvas.height;

            finalCompositeContext.drawImage(compositeCanvas, 0, 0);
            finalCompositeContext.drawImage(thumbnailCanvas, 0, 0);

            // Convert to image data and prepare for download
            const imgData = canvas.toDataURL("image/jpeg", 1.0);

            // Create thumbnail animation and download logic
            const thumbnailImage = new Image();
            thumbnailImage.src = imgData;
            thumbnailImage.style.position = "fixed";
            thumbnailImage.style.width = thumbnailSize + "px";
            thumbnailImage.style.height = thumbnailSize + "px";
            thumbnailImage.style.borderRadius = "10px";
            thumbnailImage.style.transition = "all 1s ease";
            thumbnailImage.style.border = "2px solid #1677ff";
            const thumbnailMarginRight = 30;
            thumbnailImage.style.top = thumbnailMargin + "px";
            thumbnailImage.style.right = thumbnailMargin + "px";

            document.body.appendChild(thumbnailImage);

            const finalX = window.innerWidth - thumbnailSize - thumbnailMarginRight;
            const finalY = window.innerHeight - thumbnailSize - thumbnailMargin;

            setTimeout(() => {
                thumbnailImage.style.top = finalY + "px";
                thumbnailImage.style.left = finalX + "px";
                setTimeout(() => {
                    document.body.removeChild(thumbnailImage);
                    downloadImage(imgData, "screenshot.jpg");
                }, 1000);
            }, 100);
        });
    };

    // Screenshot capture logic (modified to store base64, not download)
    const captureScreenshotForReport = () => {
    const canvas = renderer.domElement;

    requestAnimationFrame(() => {
        // Create composite canvas for the main scene
        const compositeCanvas = document.createElement("canvas");
        const compositeContext = compositeCanvas.getContext("2d");
        compositeCanvas.width = canvas.width;
        compositeCanvas.height = canvas.height;
        compositeContext.drawImage(canvas, 0, 0);

        // Optional: Thumbnail preview animation (keep your nice visual cue)
        const imgData = canvas.toDataURL("image/jpeg", 1.0);

        // Save screenshot globally for report use
        window.latestScreenshot = imgData;
        console.log("✅ Screenshot captured and stored in memory for report.");

        // ---- Visual thumbnail animation (unchanged) ----
        const thumbnailSize = 100;
        const thumbnailMargin = 30;
        const thumbnailImage = new Image();
        thumbnailImage.src = imgData;
        thumbnailImage.style.position = "fixed";
        thumbnailImage.style.width = thumbnailSize + "px";
        thumbnailImage.style.height = thumbnailSize + "px";
        thumbnailImage.style.borderRadius = "10px";
        thumbnailImage.style.transition = "all 1s ease";
        thumbnailImage.style.border = "2px solid #1677ff";
        thumbnailImage.style.top = thumbnailMargin + "px";
        thumbnailImage.style.right = thumbnailMargin + "px";
        thumbnailImage.style.zIndex = 9999;

        document.body.appendChild(thumbnailImage);

        const finalX = window.innerWidth - thumbnailSize - thumbnailMargin;
        const finalY = window.innerHeight - thumbnailSize - thumbnailMargin;

        setTimeout(() => {
        thumbnailImage.style.top = finalY + "px";
        thumbnailImage.style.left = finalX + "px";
        setTimeout(() => {
            document.body.removeChild(thumbnailImage);
        }, 1000);
        }, 100);
    });
    };

    // Sync Recoil selected indices with ref
    useEffect(() => {
        selectedIndicesRef.current = selectedIndices;
    }, [selectedIndices]);

    // Scene initialization and render loop
    useEffect(() => {
        VARS.Current_Project = currentDesign;

        clearUndoStack();
        // Append renderers to DOM
        refContainer.current && refContainer.current.appendChild(renderer.domElement);
        refContainer.current.appendChild(labelRenderer.domElement);
        refContainer.current.appendChild(cubeManager.cubeRenderer.domElement);

        // Set styles for renderers
        renderer.domElement.style.position = 'absolute';
        cubeManager.cubeRenderer.domElement.style.position = 'absolute';
        renderer.domElement.style.top = '0';

        // Position the cube renderer at the bottom
        const top = renderer.domElement.height - 210;
        cubeManager.cubeRenderer.domElement.style.top = top.toString() + "px";

        // Set z-index layering
        renderer.domElement.style.zIndex = '1';
        cubeManager.cubeRenderer.domElement.style.zIndex = '2';
        labelRenderer.domElement.style.zIndex = '3';

        // Load saved design data
        loadDrawings(scene, camera, setPolygons, setObstacles, setPanels, panelList, addSetback, setInputValues, setAvailableArea);

        // Sync label counters with polygon data
        VARS.measurementLabelCounter = POLYGONS.length - 1;
        VARS.setbackLabelCounter = POLYGONS.length - 1;

        // Animation loop
        const animate = () => {
            requestAnimationFrame(animate);
            cubeManager.viewCube.update();
            cubeManager.compass.update();
            renderer.render(scene, camera);
            labelRenderer.render(scene, camera);
            cubeManager.cubeRenderer.render(cubeManager.cubeScene, cubeManager.cubeCamera);
        };
        animate();
    }, [renderer, currentDesign, cubeManager.cubeRenderer]);

    // Set up mouse and resize event handlers
    useEffect(() => {
        mouseEventHandlers = new MouseEventHandlers(
            SceneManager,
            currentDrawingModeRef,
            obstacleTypeRef,
            updateDrawingMode,
            polygons,
            setPolygons,
            setObstacles,
            deleteObstacle,
            setPanels,
            setDeletePanels,
            deletePanels,
            setDeleteConfirmationVisible,
            selectedIndices,
            setSelectedIndices,
            setPolygonDetails,
            modelMeshs,
            panel,
            setPanel,
            setShowPanelSettings,
            mouseDown,
            setMouseDown,
            panelList,
            arePanelsOK,
            setPanelsOK
        );

        // Bind mouse and resize handlers
        const mousedownHandler = mouseEventHandlers.handleMouseDown.bind(mouseEventHandlers);
        const mousemoveHandler = mouseEventHandlers.handleMouseMove.bind(mouseEventHandlers);
        const mouseupHandler = mouseEventHandlers.handleMouseUp.bind(mouseEventHandlers);
        const dblclickHandler = mouseEventHandlers.handleDoubleClick.bind(mouseEventHandlers);
        const resizeHandler = SceneManager.handleResize.bind(SceneManager);
        const compassResizeHandler = cubeManager.handleResize.bind(cubeManager);

        // Register event listeners
        window.addEventListener('mousedown', mousedownHandler);
        window.addEventListener('mousemove', mousemoveHandler);
        window.addEventListener('mouseup', mouseupHandler);
        window.addEventListener('dblclick', dblclickHandler);
        window.addEventListener("resize", resizeHandler);
        window.addEventListener("resize", compassResizeHandler);

        // Cleanup on unmount
        return () => {
            window.removeEventListener('mousedown', mousedownHandler);
            window.removeEventListener('mousemove', mousemoveHandler);
            window.removeEventListener('mouseup', mouseupHandler);
            window.removeEventListener('dblclick', dblclickHandler);
            window.removeEventListener("resize", resizeHandler);
            window.removeEventListener("resize", compassResizeHandler);
        };
    }, [currentDrawingModeRef.current, selectedIndices]);

    // Register global screenshot trigger
    useEffect(() => {
        window.captureScreenshot = captureScreenshot;
        window.captureScreenshotForReport = captureScreenshotForReport
        return () => {
            window.captureScreenshot = null;
            window.captureScreenshotForReport = null
        };
    }, [captureScreenshot, captureScreenshotForReport]);

    // Render the canvas container
    return (
  <div
    id="canvas"
    ref={refContainer}
    style={{ width: "100%" }} // or even no style at all
  />
);


}
